import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { customersAPI, customersKycAPI, saleMediaAPI, fileUrl, ASSET_BASE } from '../services/api';
import ProfileTabs from '../components/branch/ProfileTabs';
import { formatCurrency as fmt } from '../utils/format';

function emiStatusBadge(status) {
  const map = { active: 'badge-active', completed: 'badge-completed', overdue: 'badge-overdue', defaulted: 'badge-overdue' };
  return `chip ${map[status] || 'badge-pending'}`;
}

function photoUrlForRow(row, salePhotos) {
  if (!salePhotos?.length || !row) return null;
  const emiId = row.emiId ? String(row.emiId) : '';
  const byRef = salePhotos.find((p) => emiId && String(p.saleRef || '') === emiId);
  if (byRef?.photoUrl) return byRef.photoUrl;
  if (!row.date) return salePhotos[0]?.photoUrl || null;
  const day = new Date(row.date).toDateString();
  const byDay = salePhotos.find((p) => p.capturedAt && new Date(p.capturedAt).toDateString() === day);
  return (byDay || salePhotos[0])?.photoUrl || null;
}

function toFullPhotoUrl(url) {
  if (!url) return '';
  return url.startsWith('http') ? url : `${ASSET_BASE}/${url.replace(/^\//, '')}`;
}

const TABS = [
  { id: 'overview',   label: 'Overview' },
  { id: 'purchases',  label: 'Purchases' },
  { id: 'sold',       label: 'Sold to shop' },
  { id: 'emi',        label: 'EMI Plans' },
  { id: 'witness',    label: 'Witness' },
  { id: 'documents',  label: 'Documents' },
];

export default function CustomerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('overview');
  const [text, setText] = useState({ aadhaarNumber: '', panNumber: '' });
  const [info, setInfo] = useState({ name: '', phone: '', email: '', city: '', address: '', notes: '' });
  const [witness, setWitness] = useState({ name: '', phone: '', relationship: 'Friend' });
  const [files, setFiles] = useState({ customerPhoto: null, aadhaarFront: null, aadhaarBack: null, pan: null });
  const [toast, setToast] = useState('');
  const [markWitnessContact, setMarkWitnessContact] = useState(false);
  const [salePhotos, setSalePhotos] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [editOverview, setEditOverview] = useState(false);
  const [editDocuments, setEditDocuments] = useState(false);

  const profileId = String(id || '').trim();

  const load = async () => {
    setErr('');
    try {
      const res = await customersAPI.get(profileId);
      if (!res.data?.customer) { setErr('Invalid profile response.'); return; }
      setData(res.data);
      const c = res.data.customer;
      setText({ aadhaarNumber: c?.branchKyc?.aadhaarNumber || '', panNumber: c?.branchKyc?.panNumber || '' });
      setInfo({ name: c.name || '', phone: c.phone || '', email: c.email || '', city: c.city || '', address: c.address || '', notes: c.notes || '' });
      const w0 = c?.branchKyc?.witnesses?.[0];
      if (w0) setWitness({ name: w0.name || '', phone: w0.phone || '', relationship: w0.relationship || 'Friend' });
      else setWitness({ name: '', phone: '', relationship: 'Friend' });
      setMarkWitnessContact(!!c?.isWitnessContact);
    } catch (e) {
      const msg = e.response?.data?.message || e.message || 'Failed to load customer';
      const path = e.response?.data?.path;
      setErr(path ? `${msg} (${path})` : msg);
    }
  };

  useEffect(() => {
    if (profileId) {
      load();
      saleMediaAPI.getForCustomer(profileId)
        .then(r => setSalePhotos(r.data.photos || []))
        .catch(() => setSalePhotos([]));
    }
    // eslint-disable-next-line
  }, [profileId]);

  const flash = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2400); };

  const saveInfo = async (e) => {
    e.preventDefault();
    setSaving(true); setErr('');
    try {
      await customersAPI.patch(profileId, { name: info.name, email: info.email, city: info.city, address: info.address, notes: info.notes });
      flash('Profile info saved');
      setEditOverview(false);
      await load();
    } catch (e2) { setErr(e2.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const saveKyc = async (e) => {
    e.preventDefault();
    setSaving(true); setErr('');
    try {
      await customersAPI.patch(profileId, {
        aadhaarNumber: text.aadhaarNumber,
        panNumber: text.panNumber,
        isWitnessContact: markWitnessContact
      });
      flash('KYC details saved');
      setEditOverview(false);
      await load();
    } catch (e2) { setErr(e2.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const saveFiles = async (e) => {
    e.preventDefault();
    if (!files.customerPhoto && !files.aadhaarFront && !files.aadhaarBack && !files.pan) {
      setErr('Select at least one file to upload.'); return;
    }
    setSaving(true); setErr('');
    try {
      const fd = new FormData();
      if (files.customerPhoto) fd.append('customerPhoto', files.customerPhoto);
      if (files.aadhaarFront)  fd.append('aadhaarFront', files.aadhaarFront);
      if (files.aadhaarBack)   fd.append('aadhaarBack', files.aadhaarBack);
      if (files.pan)           fd.append('pan', files.pan);
      fd.append('witnessesJson', JSON.stringify([{ name: witness.name, phone: witness.phone, relationship: witness.relationship }]));
      await customersKycAPI.upload(profileId, fd);
      setFiles({ customerPhoto: null, aadhaarFront: null, aadhaarBack: null, pan: null });
      flash('Documents uploaded successfully');
      setEditDocuments(false);
      await load();
    } catch (e2) { setErr(e2.response?.data?.message || 'Upload failed'); }
    finally { setSaving(false); }
  };

  if (err && !data) {
    return (
      <div className="form-card">
        <p className="text-danger">{err}</p>
        <Link to="/customers" className="btn btn-outline-secondary">← Back to Customers</Link>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" />
        <div className="text-muted mt-3">Loading profile…</div>
      </div>
    );
  }

  const c = data.customer;
  const k = data.kycSummary || {};
  const stats = data.stats || {};
  const emis = data.emis || [];
  const docs = c.branchKyc?.documents || {};
  const salesHistory = data.salesHistory || [];
  const witnessedEmis = data.witnessedEmis || [];
  let purchaseRows = data.purchaseRows || [];
  if (!purchaseRows.length) {
    const linked = new Set();
    purchaseRows = salesHistory.map((s) => {
      if (s.emiPlanId) linked.add(String(s.emiPlanId));
      return {
        rowKey: `sale-${s._id}`,
        date: s.date,
        productName: s.productName,
        category: s.category,
        amount: s.salePrice,
        mode: s.isEmiSale || s.saleType === 'emi' ? 'EMI' : 'Cash',
        isEmi: !!(s.isEmiSale || s.saleType === 'emi'),
        downPayment: s.downPayment,
        emiMonths: s.emiMonths,
        witnessName: s.witnessName,
        emiId: s.emiPlanId ? String(s.emiPlanId) : null
      };
    });
    emis.forEach((e) => {
      if (linked.has(String(e._id))) return;
      purchaseRows.push({
        rowKey: `emi-${e._id}`,
        date: e.saleInfo?.saleDate || e.startDate,
        productName: e.productInfo?.name,
        category: e.productInfo?.category,
        amount: e.saleInfo?.salePrice || e.emiPlan?.totalAmount,
        mode: 'EMI',
        isEmi: true,
        downPayment: e.emiPlan?.downPayment,
        emiMonths: e.emiPlan?.months,
        witnessName: e.saleInfo?.witness?.witnessName,
        emiId: String(e._id)
      });
    });
    purchaseRows.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
  }
  const rewardTransactions = data.rewardTransactions || [];
  const witnessRewardHistory = data.witnessRewardHistory || [];
  const kycChecklist = k.checklist || [];
  const sellerHistory = data.sellerIntakeHistory || {};
  const soldIntakes = sellerHistory.intakes || [];
  const phonesSoldProfile = c.phonesSoldToShop || [];

  const docFields = [
    { key: 'customerPhoto', label: 'Customer photo' },
    { key: 'aadhaarFront',  label: 'Aadhaar front' },
    { key: 'aadhaarBack',   label: 'Aadhaar back' },
    { key: 'pan',           label: 'PAN card' }
  ];

  return (
    <div>
      {/* ── Header ── */}
      <div className="d-flex flex-wrap justify-content-between align-items-start mb-3">
        <div className="d-flex align-items-center gap-3">
          <button className="btn btn-sm btn-outline-secondary" onClick={() => navigate(-1)}>← Back</button>
          <div>
            <h4 className="mb-0 fw-700 d-flex align-items-center flex-wrap gap-2">
              {c.name}
              {k.fullKycComplete && <span className="chip badge-kyc-complete" style={{ fontSize: 11 }}>KYC Complete</span>}
              {!k.fullKycComplete && k.emiKycReady && <span className="chip badge-kyc-complete" style={{ fontSize: 11 }}>EMI Ready</span>}
              {!k.emiKycReady && <span className="chip badge-kyc-pending" style={{ fontSize: 11 }}>KYC Pending</span>}
              {k.kycStatus === 'partial' && !k.fullKycComplete && (
                <span className="chip badge-kyc-pending" style={{ fontSize: 11 }}>Partial KYC</span>
              )}
            </h4>
            <div className="text-muted" style={{ fontSize: 13 }}>
              {c.phone}{c.city ? ` · ${c.city}` : ''} · Added {c.createdAt ? new Date(c.createdAt).toLocaleDateString('en-IN') : '—'}
            </div>
          </div>
        </div>
        <Link to="/customers" className="btn btn-sm btn-link mt-2 mt-md-0">All Customers</Link>
      </div>

      {toast && <div className="alert-soft success mb-3">{toast}</div>}
      {err   && <div className="alert-soft danger mb-3">{err} <button className="btn btn-sm btn-link" onClick={() => setErr('')}>×</button></div>}

      {/* ── Stats bar ── */}
      <div className="row g-2 mb-3">
        <div className="col-6 col-md-3">
          <div className="stat-tile" style={{ borderLeft: '4px solid #0d6efd' }}>
            <div className="stat-label">Total Purchases</div>
            <div className="stat-value" style={{ color: '#0d6efd', fontSize: 22 }}>{stats.totalPurchases || 0}</div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="stat-tile" style={{ borderLeft: '4px solid #198754' }}>
            <div className="stat-label">Total Spent</div>
            <div className="stat-value" style={{ color: '#198754', fontSize: 22 }}>{fmt(stats.totalSpent)}</div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="stat-tile" style={{ borderLeft: '4px solid #7c3aed' }}>
            <div className="stat-label">Active EMIs</div>
            <div className="stat-value" style={{ color: stats.activeEmiCount ? '#b91c1c' : '#198754', fontSize: 22 }}>{stats.activeEmiCount || 0}</div>
            <div className="stat-sub">Pending {fmt(stats.totalPending)}</div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="stat-tile" style={{ borderLeft: '4px solid #b45309' }}>
            <div className="stat-label">Reward Points</div>
            <div className="stat-value" style={{ color: '#b45309', fontSize: 22 }}>{stats.rewardPoints || 0}</div>
          </div>
        </div>
      </div>

      {/* ── Tabs ── */}
      <ProfileTabs
        tabs={TABS.map((t) => ({
          ...t,
          count: t.id === 'emi' ? emis.length : t.id === 'witness' ? witnessedEmis.length || undefined : undefined,
          dot: t.id === 'documents' ? (k.fullKycComplete ? 'green' : 'amber') : undefined
        }))}
        activeTab={tab}
        onTabChange={setTab}
      />

      {/* ── Tab: Overview ── */}
      {tab === 'overview' && (
        <div className="row g-3">
          <div className="col-lg-6">
            <div className="section-head d-flex justify-content-between align-items-center">
              <span>Personal Information</span>
              {!editOverview ? (
                <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setEditOverview(true)}>Edit</button>
              ) : (
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => { setEditOverview(false); setInfo({ name: c.name || '', phone: c.phone || '', email: c.email || '', city: c.city || '', address: c.address || '', notes: c.notes || '' }); }}>Cancel</button>
              )}
            </div>
            <div className="section-body">
              {!editOverview ? (
                <dl className="row mb-0" style={{ fontSize: 14 }}>
                  <dt className="col-4 text-muted">Name</dt><dd className="col-8 fw-600">{info.name || '—'}</dd>
                  <dt className="col-4 text-muted">Phone</dt><dd className="col-8">{info.phone || '—'}</dd>
                  <dt className="col-4 text-muted">Email</dt><dd className="col-8">{info.email || '—'}</dd>
                  <dt className="col-4 text-muted">City</dt><dd className="col-8">{info.city || '—'}</dd>
                  <dt className="col-4 text-muted">Address</dt><dd className="col-8">{info.address || '—'}</dd>
                  <dt className="col-4 text-muted">Notes</dt><dd className="col-8">{info.notes || '—'}</dd>
                </dl>
              ) : (
                <form onSubmit={saveInfo} className="row g-2">
                  <div className="col-md-6">
                    <label className="form-label">Full name</label>
                    <input className="form-control" value={info.name} onChange={e => setInfo(f => ({ ...f, name: e.target.value }))} required />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Phone</label>
                    <input className="form-control" value={info.phone} disabled style={{ background: '#f9fafb' }} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Email</label>
                    <input className="form-control" type="email" value={info.email} onChange={e => setInfo(f => ({ ...f, email: e.target.value }))} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">City</label>
                    <input className="form-control" value={info.city} onChange={e => setInfo(f => ({ ...f, city: e.target.value }))} />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Address</label>
                    <input className="form-control" value={info.address} onChange={e => setInfo(f => ({ ...f, address: e.target.value }))} />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Notes</label>
                    <textarea className="form-control" rows={2} value={info.notes} onChange={e => setInfo(f => ({ ...f, notes: e.target.value }))} />
                  </div>
                  <div className="col-12 mt-1">
                    <button className="btn btn-primary btn-sm" type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save Info'}</button>
                  </div>
                </form>
              )}
            </div>
          </div>

          <div className="col-lg-6">
            <div className="section-head green d-flex justify-content-between align-items-center">
              <span>KYC Numbers</span>
              {!editOverview ? (
                <button type="button" className="btn btn-sm btn-outline-success" onClick={() => setEditOverview(true)}>Edit</button>
              ) : null}
            </div>
            <div className="section-body">
              {!editOverview ? (
                <>
                  <dl className="row mb-2" style={{ fontSize: 14 }}>
                    <dt className="col-5 text-muted">Aadhaar</dt><dd className="col-7 fw-600">{text.aadhaarNumber || '—'}</dd>
                    <dt className="col-5 text-muted">PAN</dt><dd className="col-7 fw-600">{text.panNumber || '—'}</dd>
                  </dl>
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" checked={markWitnessContact} disabled />
                    <label className="form-check-label text-muted" style={{ fontSize: 13 }}>
                      Quick reference in witness search {markWitnessContact ? '(on)' : '(off)'}
                    </label>
                  </div>
                </>
              ) : (
                <form onSubmit={saveKyc} className="row g-2">
                  <div className="col-md-6">
                    <label className="form-label">Aadhaar number</label>
                    <input
                      className="form-control"
                      inputMode="numeric"
                      maxLength={12}
                      placeholder="12-digit Aadhaar"
                      value={text.aadhaarNumber}
                      onChange={e => setText(f => ({ ...f, aadhaarNumber: e.target.value.replace(/\D/g, '').slice(0, 12) }))}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">PAN number</label>
                    <input
                      className="form-control"
                      placeholder="ABCDE1234F"
                      value={text.panNumber}
                      onChange={e => setText(f => ({ ...f, panNumber: e.target.value.toUpperCase() }))}
                    />
                  </div>
                  <div className="col-12">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="witnessMark"
                        checked={markWitnessContact}
                        onChange={e => setMarkWitnessContact(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="witnessMark">
                        Quick reference contact — show in witness search on New Sale
                      </label>
                    </div>
                  </div>
                  <div className="col-12 mt-1">
                    <button className="btn btn-success btn-sm" type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save KYC'}</button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Tab: Purchases ── */}
      {tab === 'purchases' && (
        <div>
          {purchaseRows.length === 0 ? (
            <div className="empty-state">
              <div className="es-icon">🛍️</div>
              <div className="es-title">No purchases yet</div>
              <div>This customer has not made any purchases from this branch.</div>
              <button className="btn btn-primary btn-sm mt-3" onClick={() => navigate(`/new-sale?customerId=${profileId}`)}>
                Create First Sale
              </button>
            </div>
          ) : (
            <div className="table-card">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Product</th>
                      <th>Mode</th>
                      <th className="text-end">Amount</th>
                      <th style={{ width: 90 }}>Photo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchaseRows.map((row) => {
                      const photoPath = photoUrlForRow(row, salePhotos);
                      const isEmi = row.isEmi || row.mode === 'EMI';
                      const canOpenEmi = isEmi && row.emiId;
                      return (
                        <tr
                          key={row.rowKey}
                          style={canOpenEmi ? { cursor: 'pointer' } : undefined}
                          title={canOpenEmi ? 'EMI Collections mein kholo' : undefined}
                          onClick={() => {
                            if (canOpenEmi) navigate(`/emi?emiId=${row.emiId}`);
                          }}
                        >
                          <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                            {row.date ? new Date(row.date).toLocaleDateString('en-IN') : '—'}
                          </td>
                          <td>
                            <strong style={{ fontSize: 13 }}>{row.productName || '—'}</strong>
                            {row.category && <div className="text-muted" style={{ fontSize: 11 }}>{row.category}</div>}
                          </td>
                          <td>
                            <span className={`chip ${isEmi ? 'badge-emi' : 'badge-cash'}`}>{row.mode || 'Cash'}</span>
                            {isEmi && row.downPayment > 0 && (
                              <div className="text-muted" style={{ fontSize: 11, marginTop: 2 }}>
                                DP: {fmt(row.downPayment)} + {row.emiMonths || '?'}× kist
                              </div>
                            )}
                            {isEmi && row.witnessName && (
                              <div className="text-muted" style={{ fontSize: 11 }}>Witness: {row.witnessName}</div>
                            )}
                          </td>
                          <td className="text-end"><strong>{fmt(row.amount)}</strong></td>
                          <td>
                            {photoPath ? (
                              <button
                                type="button"
                                className="btn btn-link btn-sm p-0"
                                style={{ fontSize: 12 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setLightbox(toFullPhotoUrl(photoPath));
                                }}
                              >
                                View
                              </button>
                            ) : (
                              <span className="text-muted" style={{ fontSize: 12 }}>—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Sold to shop (Old Mobile buy-back) ── */}
      {tab === 'sold' && (
        <div>
          {soldIntakes.length === 0 && phonesSoldProfile.length === 0 ? (
            <div className="empty-state">
              <div className="es-icon">📱</div>
              <div className="es-title">No phones sold to shop</div>
              <div>Old phone valuation se jab customer apna phone bechta hai, yahan record dikhega.</div>
            </div>
          ) : (
            <>
              <div className="row g-2 mb-3">
                <div className="col-6 col-md-4">
                  <div className="stat-tile" style={{ borderLeft: '4px solid #b45309' }}>
                    <div className="stat-label">Units sold to shop</div>
                    <div className="stat-value" style={{ fontSize: 22 }}>{sellerHistory.totalUnitsSoldToShop ?? soldIntakes.length}</div>
                  </div>
                </div>
                <div className="col-6 col-md-4">
                  <div className="stat-tile" style={{ borderLeft: '4px solid #198754' }}>
                    <div className="stat-label">Intake points earned</div>
                    <div className="stat-value" style={{ fontSize: 22 }}>{sellerHistory.totalIntakePointsEarned ?? 0}</div>
                  </div>
                </div>
                <div className="col-12 col-md-4">
                  <div className="stat-tile" style={{ borderLeft: '4px solid #7c3aed' }}>
                    <div className="stat-label">Current reward balance</div>
                    <div className="stat-value" style={{ fontSize: 22 }}>{stats.rewardPoints ?? c.rewardPoints ?? 0}</div>
                  </div>
                </div>
              </div>
              <div className="table-card">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Phone / model</th>
                        <th>Amount (shop ne liya)</th>
                        <th>Points</th>
                        <th>Photo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(soldIntakes.length ? soldIntakes : phonesSoldProfile).map((row, idx) => {
                        const dt = row.sellerIntakeRewardAt || row.date || row.addedDate;
                        const photo = row.sellerIntakePhotoUrl || row.photoUrl;
                        const photoSrc = photo ? (photo.startsWith('http') ? photo : fileUrl(photo)) : '';
                        return (
                          <tr key={row.productId ? `${row.productId}-${row.imeiIndex ?? idx}` : idx}>
                            <td>{dt ? new Date(dt).toLocaleDateString('en-IN') : '—'}</td>
                            <td>
                              <div className="fw-600">{row.productName || '—'}</div>
                              {row.imei1 && <div className="text-muted small">IMEI …{String(row.imei1).slice(-6)}</div>}
                            </td>
                            <td><strong>{fmt(row.sellingAmount ?? row.buyingPrice)}</strong></td>
                            <td>{row.pointsAwarded || row.rewardPointsEarned ? '+100' : '—'}</td>
                            <td>
                              {photoSrc ? (
                                <img
                                  src={photoSrc}
                                  alt="Intake"
                                  style={{ width: 56, height: 42, objectFit: 'cover', borderRadius: 6, cursor: 'pointer' }}
                                  onClick={() => setLightbox(photoSrc)}
                                />
                              ) : (
                                '—'
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Tab: EMI Plans ── */}
      {tab === 'emi' && (
        <div>
          {emis.length === 0 ? (
            <div className="empty-state">
              <div className="es-icon">📋</div>
              <div className="es-title">No EMI plans</div>
              <div>No EMI accounts found for this customer.</div>
            </div>
          ) : (
            <div className="table-card">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Sale Date</th>
                      <th>Status</th>
                      <th>Progress</th>
                      <th>Monthly</th>
                      <th>Next Due</th>
                      <th>KYC at Sale</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emis.map(e => {
                      const total = e.emiPlan?.months || 0;
                      const paid = e.paidCount || 0;
                      return (
                        <tr key={e._id}>
                          <td><strong>{e.productInfo?.name || '—'}</strong></td>
                          <td style={{ fontSize: 12 }}>{e.saleInfo?.saleDate ? new Date(e.saleInfo.saleDate).toLocaleDateString('en-IN') : '—'}</td>
                          <td><span className={emiStatusBadge(e.status)}>{e.status}</span></td>
                          <td>
                            <div style={{ fontSize: 13 }}>{paid}/{total} paid</div>
                            <div className="progress" style={{ height: 4, borderRadius: 4, marginTop: 4, background: '#e5e7eb' }}>
                              <div className="progress-bar bg-primary" style={{ width: `${total ? Math.round((paid/total)*100) : 0}%`, borderRadius: 4 }} />
                            </div>
                          </td>
                          <td>{fmt(e.emiPlan?.monthlyAmount)}</td>
                          <td style={{ fontSize: 12 }}>{e.nextPaymentDate ? new Date(e.nextPaymentDate).toLocaleDateString('en-IN') : '—'}</td>
                          <td>
                            {e.kycAtSale?.kycComplete
                              ? <span className="chip badge-kyc-complete">Complete</span>
                              : <span className="chip badge-kyc-pending">Incomplete</span>}
                            {e.kycAtSale?.witnessOnFile && <span className="chip badge-witness ms-1">W</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Witness ── */}
      {tab === 'witness' && (
        <div>
          {/* Quick-reference toggle */}
          <div className="section-head green d-flex align-items-center justify-content-between">
            <span>Quick Reference Contact</span>
            <div className="form-check mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                id="wContactGlobal"
                checked={markWitnessContact}
                onChange={async (e) => {
                  const val = e.target.checked;
                  setMarkWitnessContact(val);
                  try {
                    await import('../services/api').then(m =>
                      m.customersAPI.patch(profileId, { isWitnessContact: val })
                    );
                    flash(val ? 'Marked as quick reference contact' : 'Removed from quick reference');
                  } catch { flash('Could not update'); }
                }}
              />
              <label className="form-check-label" htmlFor="wContactGlobal" style={{ fontSize: 13 }}>
                Show in witness search on New Sale
              </label>
            </div>
          </div>
          <div className="section-body mb-4" style={{ fontSize: 13, color: '#6b7280' }}>
            Quick reference contacts are normal customers — uncheck anytime. Witness is always per-sale, not fixed to profile.
          </div>

          {/* Sales this customer was a BUYER with witness */}
          {emis.length > 0 && (
            <>
              <div className="section-head blue">Their EMI purchases — witness at each sale</div>
              <div className="section-body p-0 mb-4">
                <div className="table-responsive">
                  <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Sale date</th>
                        <th>Witness</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {emis.map(e => (
                        <tr key={e._id}>
                          <td className="fw-600">{e.productInfo?.name || '—'}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>{e.saleInfo?.saleDate ? new Date(e.saleInfo.saleDate).toLocaleDateString('en-IN') : '—'}</td>
                          <td>
                            {e.saleInfo?.witness?.witnessName
                              ? <><span className="fw-600">{e.saleInfo.witness.witnessName}</span><br /><span className="text-muted">{e.saleInfo.witness.witnessPhone}</span></>
                              : <span className="text-muted">—</span>}
                          </td>
                          <td><span className={emiStatusBadge(e.status)}>{e.status}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* Sales this customer WITNESSED for others */}
          <div className="section-head" style={{ background: '#7c3aed', color: '#fff' }}>
            Sales they've witnessed for others
            {(witnessRewardHistory.length || witnessedEmis.length) > 0 && (
              <span style={{ marginLeft: 8, fontSize: 12, background: 'rgba(255,255,255,0.25)', padding: '2px 8px', borderRadius: 12 }}>
                {witnessRewardHistory.length || witnessedEmis.length}
              </span>
            )}
          </div>
          {(stats.witnessPointsEarned > 0 || stats.rewardPoints > 0) && (
            <div className="section-body mb-0 pb-2" style={{ fontSize: 13 }}>
              <span className="me-3">
                Witness points (estimated): <strong style={{ color: '#16a34a' }}>+{stats.witnessPointsEarned || 0}</strong>
              </span>
              <span>
                Total reward balance: <strong style={{ color: '#b45309' }}>{stats.rewardPoints || 0}</strong>
              </span>
            </div>
          )}
          <div className="section-body p-0">
            {(witnessRewardHistory.length || witnessedEmis.length) === 0 ? (
              <div className="text-muted p-3">
                This customer has not been a witness for any sale yet. When they witness someone else's EMI purchase, it will appear here.
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Product</th>
                      <th>Buyer</th>
                      <th className="text-end">Sale ₹</th>
                      <th className="text-end">Points</th>
                      <th>EMI</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(witnessRewardHistory.length ? witnessRewardHistory : witnessedEmis).map((w) => {
                      const saleDate = w.date || w.saleDate || w.saleInfo?.saleDate;
                      const productName = w.productName || w.productInfo?.name || '—';
                      const buyerName = w.buyerName || w.customer?.name || '—';
                      const buyerPhone = w.buyerPhone || w.customer?.phone || '';
                      const buyerId = w.buyerCustomerId || w.customerId;
                      const salePrice = w.saleAmount ?? w.salePrice ?? w.saleInfo?.salePrice ?? w.emiPlan?.totalAmount ?? 0;
                      const months = w.emiMonths ?? w.emiPlan?.months;
                      const pts = w.pointsEarned ?? 0;
                      const rowKey = w.emiId || w._id;
                      return (
                        <tr key={rowKey}>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {saleDate ? new Date(saleDate).toLocaleDateString('en-IN') : '—'}
                          </td>
                          <td className="fw-600">{productName}</td>
                          <td>
                            {buyerId ? (
                              <Link to={`/customers/${buyerId}`} className="text-decoration-none">
                                <div className="fw-600">{buyerName}</div>
                              </Link>
                            ) : (
                              <div className="fw-600">{buyerName}</div>
                            )}
                            <div className="text-muted" style={{ fontSize: 11 }}>{buyerPhone}</div>
                          </td>
                          <td className="text-end">{fmt(salePrice)}</td>
                          <td className="text-end fw-700" style={{ color: pts > 0 ? '#16a34a' : '#9ca3af' }}>
                            {pts > 0 ? `+${pts}` : '—'}
                          </td>
                          <td className="text-muted">{months ? `${months}m` : '—'}</td>
                          <td><span className={emiStatusBadge(w.outcome || w.status)}>{w.outcome || w.status}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {rewardTransactions.length > 0 && (
            <>
              <div className="section-head mt-4" style={{ background: '#b45309', color: '#fff' }}>
                Reward points history (last 30)
              </div>
              <div className="section-body p-0">
                <div className="table-responsive">
                  <table className="table table-sm mb-0" style={{ fontSize: 12 }}>
                    <thead>
                      <tr><th>Date</th><th>Reason</th><th className="text-end">+pts</th><th className="text-end">Balance</th></tr>
                    </thead>
                    <tbody>
                      {rewardTransactions.map((t, i) => (
                        <tr key={i}>
                          <td style={{ whiteSpace: 'nowrap' }}>{t.date ? new Date(t.date).toLocaleDateString('en-IN') : '—'}</td>
                          <td className="text-muted">{t.reason || '—'}</td>
                          <td className="text-end fw-700" style={{ color: '#16a34a' }}>+{t.points}</td>
                          <td className="text-end">{t.newBalance}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Tab: Documents ── */}
      {tab === 'documents' && (
        <div>
          <div className="section-head amber d-flex justify-content-between align-items-center">
            <span>KYC Documents</span>
            {!editDocuments ? (
              <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setEditDocuments(true)}>Edit / Upload</button>
            ) : (
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => { setEditDocuments(false); setFiles({ customerPhoto: null, aadhaarFront: null, aadhaarBack: null, pan: null }); }}>Cancel</button>
            )}
          </div>
          <div className="section-body">
            {/* KYC checklist — what's done / missing */}
            {kycChecklist.length > 0 && (
              <div className="mb-4">
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-2">
                  <strong style={{ fontSize: 14 }}>KYC Checklist</strong>
                  <span className={`chip ${k.fullKycComplete ? 'badge-kyc-complete' : 'badge-kyc-pending'}`}>
                    {k.completedCount || 0}/{k.totalCount || kycChecklist.length} complete
                    {k.missingCount > 0 && !k.fullKycComplete && ` · ${k.missingCount} pending`}
                  </span>
                </div>
                <div className="row g-2">
                  {kycChecklist.map((item) => (
                    <div className="col-md-6" key={item.key}>
                      <div
                        className="d-flex align-items-center gap-2 p-2 rounded"
                        style={{
                          border: `1px solid ${item.done ? '#bbf7d0' : '#fecaca'}`,
                          background: item.done ? '#f0fdf4' : '#fef2f2',
                          fontSize: 13
                        }}
                      >
                        <span style={{ fontSize: 16 }}>{item.done ? '✓' : '○'}</span>
                        <span className={item.done ? 'text-success' : 'text-danger'}>
                          {item.label}
                          {!item.required && <span className="text-muted"> (optional)</span>}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                {k.missingRequired?.length > 0 && !k.fullKycComplete && (
                  <div className="alert-soft danger mt-3 mb-0" style={{ fontSize: 13 }}>
                    Missing for full KYC: {k.missingRequired.join(', ')}
                  </div>
                )}
              </div>
            )}

            <div className="row g-3">
              {docFields.map(({ key, label }) => (
                <div className="col-md-6" key={key}>
                  <label className="form-label">{label}</label>
                  <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                    {docs[key] ? (
                      <>
                        <a href={fileUrl(docs[key])} target="_blank" rel="noopener noreferrer" className="chip badge-kyc-complete">
                          On file · View ↗
                        </a>
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0"
                          onClick={() => setLightbox(fileUrl(docs[key]))}
                        >
                          Preview
                        </button>
                      </>
                    ) : (
                      <span className="chip badge-kyc-pending">Not uploaded</span>
                    )}
                  </div>
                  {docs[key] && (
                    <img
                      src={fileUrl(docs[key])}
                      alt={label}
                      style={{ maxWidth: 160, maxHeight: 120, objectFit: 'cover', borderRadius: 8, border: '1px solid #e5e7eb', cursor: 'zoom-in' }}
                      onClick={() => setLightbox(fileUrl(docs[key]))}
                    />
                  )}
                  {editDocuments && (
                    <input
                      type="file"
                      className="form-control form-control-sm mt-2"
                      accept="image/*,application/pdf"
                      onChange={e => setFiles(f => ({ ...f, [key]: e.target.files[0] || null }))}
                    />
                  )}
                </div>
              ))}
            </div>
            {editDocuments && (
              <form onSubmit={saveFiles} className="mt-3">
                <p className="text-muted" style={{ fontSize: 12 }}>
                  JPEG / PNG / WebP · up to 12 MB each. Sirf nayi file choose karein jo replace karni ho.
                </p>
                <button className="btn btn-primary btn-sm" type="submit" disabled={saving}>
                  {saving ? 'Uploading…' : 'Upload selected files'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* ── Lightbox ── */}
      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out' }}
        >
          <img src={lightbox} alt="Preview" style={{ maxWidth: '90vw', maxHeight: '88vh', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }} />
          <button
            onClick={() => setLightbox(null)}
            style={{ position: 'absolute', top: 18, right: 22, background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: 50, width: 36, height: 36, fontSize: 20, cursor: 'pointer' }}
          >×</button>
        </div>
      )}
    </div>
  );
}
